import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Breadcrumb, Card, Col, Divider, Row, Spin } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { toast } from "utils/toast";

import { useGetProperty } from "hooks/useGetProperty";
import { useGetTenantReportsByPropertyId } from "hooks/useTenantReports";

import LandlordDetailsSection from "js/components/Properties/PropertyDetails/PropertyDetailsSections/LandlordDetailsSection";
import PropertyDetailsSection from "js/components/Properties/PropertyDetails/PropertyDetailsSections/PropertyDetailsSection";
import RentalRequestHistorySection from "js/components/Properties/PropertyDetails/PropertyDetailsSections/RentalRequestHistorySection";
import type { PropertyDetailsData } from "js/components/Properties/types";
import { Landlord, PaginationTable } from "js/components/types";

import "styles/pages/Properties/PropertyForm.scss";

interface PropertyDetailsViewProps {
  organizationScope?: boolean;
}

const PropertyDetails = ({
  organizationScope: routeOrganizationScope = false,
}: PropertyDetailsViewProps) => {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const screen = useBreakpoint();
  const navigate = useNavigate();
  const organizationScope =
    routeOrganizationScope || searchParams.get("scope") === "organization";

  // State for child component updates
  const [propertyData, setPropertyData] = useState<PropertyDetailsData>();
  const [landlordData, setLandlordData] = useState<Landlord | {}>({});
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  // Fetch property data using TanStack Query
  const {
    data: propertyDataFromHook,
    isLoading: propertyLoading,
    error: propertyError,
  } = useGetProperty(propertyId || "", {
    enabled: !!propertyId,
    organizationScope,
  });

  // Fetch tenant reports by property ID
  const {
    data: reportsResponse,
    isLoading: reportsLoading,
    error: reportsError,
  } = useGetTenantReportsByPropertyId(pagination, propertyId || "", {
    enabled: !!propertyId,
    organizationScope,
  });

  // Derive data from hooks
  const propertyRentalHistoryData = reportsResponse?.results ?? [];
  const isLoading = propertyLoading || reportsLoading;

  // Initialize propertyData from hook data
  useEffect(() => {
    if (propertyDataFromHook) {
      setPropertyData(propertyDataFromHook);
      setLandlordData(propertyDataFromHook?.landlord ?? {});
    }
  }, [propertyDataFromHook]);

  // Update pagination total from reports response
  useEffect(() => {
    if (reportsResponse?.count !== undefined) {
      setPagination((prev) => ({ ...prev, total: reportsResponse.count }));
    }
  }, [reportsResponse]);

  useEffect(() => {
    if (propertyError) {
      toast.error("Error al obtener información del inmueble.");
    }
    if (reportsError) {
      toast.error("Error al obtener reportes del inquilino.");
    }
  }, [propertyError, reportsError]);

  if (isLoading || !propertyData) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin />
      </div>
    );
  }

  const { alias, address } = propertyData;

  return (
    <>
      <Row style={{ marginBottom: 5 }}>
        <Breadcrumb>
          <Breadcrumb.Item onClick={() => navigate(-1)}>
            <p style={{ cursor: "pointer" }}>Inmuebles</p>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            {alias ||
              `${address?.street ?? ""} ${address?.exterior_number ?? ""}`}
          </Breadcrumb.Item>
        </Breadcrumb>
      </Row>
      <Row>
        <Col className={"mb-2"} xs={24} sm={24} md={24} lg={15}>
          <Card style={{ width: "100%", height: "100%" }}>
            {/* Property Section */}
            <PropertyDetailsSection
              property={propertyData}
              setPropertyData={setPropertyData}
              readOnly={organizationScope}
            />

            {/* Landlord Section for Mobile view */}
            {!screen.lg && (
              <>
                <Divider />
                <LandlordDetailsSection
                  landlordData={landlordData}
                  setLandlordData={setLandlordData}
                  readOnly={organizationScope}
                />
              </>
            )}
          </Card>
        </Col>
        {/* Landlord Section for Desk view */}
        {screen.lg && (
          <Col
            className={"mb-2"}
            style={{ paddingLeft: 24, width: "100%" }}
            xs={24}
            sm={24}
            md={24}
            lg={9}
          >
            <Card style={{ width: "100%", height: "100%" }}>
              <LandlordDetailsSection
                landlordData={landlordData}
                setLandlordData={setLandlordData}
                readOnly={organizationScope}
              />
            </Card>
          </Col>
        )}
      </Row>
      <Row>
        <Col xs={24} sm={24} md={24} lg={24}>
          <Card>
            {/* Rental Request List */}
            <RentalRequestHistorySection
              propertyRentalHistoryData={propertyRentalHistoryData}
              propertyId={propertyId}
              readOnly={organizationScope}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PropertyDetails;
